/**
 * Phase 8 T-19 §22.A — Right-rail network settings (360 px).
 *
 * Inline-editable:
 *   - Network name
 *   - Description
 *   - Privacy radio (Anyone with link / Only members)
 *   - Direct search link (mono, 11px, copy button)
 *   - Destructive action line (text-red-600 underline, never a filled button — §16)
 */

import { useCallback, useState } from 'react';
import { Check, Copy, ExternalLink } from 'lucide-react';
import type { LibraryNetwork } from '../types';

interface NetworkSettingsRailProps {
  network: LibraryNetwork;
  onUpdate: (patch: Partial<Pick<LibraryNetwork, 'name' | 'description' | 'privacy'>>) => void;
}

function InlineEditText({
  value,
  onSave,
  tag: Tag = 'p',
  className = '',
  multiline = false,
}: {
  value: string;
  onSave: (v: string) => void;
  tag?: 'p' | 'h2';
  className?: string;
  multiline?: boolean;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);

  const commit = useCallback(() => {
    const trimmed = draft.trim();
    if (trimmed && trimmed !== value) onSave(trimmed);
    else setDraft(value);
    setEditing(false);
  }, [draft, value, onSave]);

  const handleKey = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' && (!multiline || e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        commit();
      }
      if (e.key === 'Escape') {
        setDraft(value);
        setEditing(false);
      }
    },
    [commit, multiline, value],
  );

  if (editing) {
    const sharedClass =
      'w-full rounded-[6px] border px-2 py-1 text-[13px] vscode-border vscode-bg-secondary vscode-text-primary focus:outline-none focus:ring-1 focus:ring-[#10B981]';
    return multiline ? (
      <textarea
        autoFocus
        rows={3}
        className={`${sharedClass} resize-none`}
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={handleKey}
      />
    ) : (
      <input
        autoFocus
        type="text"
        className={sharedClass}
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={handleKey}
      />
    );
  }

  return (
    <Tag
      className={`cursor-text rounded-[4px] px-1 -mx-1 transition-colors hover:vscode-bg-hover ${className}`}
      onClick={() => {
        setDraft(value);
        setEditing(true);
      }}
      title="Click to edit"
    >
      {value}
    </Tag>
  );
}

function CopyButton({ text }: { text: string }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      /* clipboard unavailable */
    }
  }, [text]);

  return (
    <button
      type="button"
      onClick={() => void handleCopy()}
      className="flex-shrink-0 rounded p-1 vscode-text-tertiary transition-colors hover:vscode-text-primary"
      aria-label={copied ? 'Copied!' : 'Copy link'}
      title={copied ? 'Copied!' : 'Copy link'}
    >
      {copied ? (
        <Check className="h-3.5 w-3.5 text-[#10B981]" aria-hidden />
      ) : (
        <Copy className="h-3.5 w-3.5" aria-hidden />
      )}
    </button>
  );
}

export function NetworkSettingsRail({ network, onUpdate }: NetworkSettingsRailProps) {
  return (
    <aside
      className="flex w-full flex-col gap-5 overflow-y-auto border-l p-5 vscode-border"
      style={{ minWidth: 0 }}
      aria-label="Network settings"
    >
      {/* Name */}
      <div>
        <p className="mb-1 text-[11px] font-semibold uppercase tracking-[0.06em] vscode-text-tertiary">
          Network name
        </p>
        <InlineEditText
          tag="h2"
          value={network.name}
          onSave={(v) => onUpdate({ name: v })}
          className="text-[14px] font-semibold vscode-text-primary"
        />
      </div>

      {/* Description */}
      <div>
        <p className="mb-1 text-[11px] font-semibold uppercase tracking-[0.06em] vscode-text-tertiary">
          Description
        </p>
        <InlineEditText
          value={network.description || 'Add a description…'}
          onSave={(v) => onUpdate({ description: v })}
          multiline
          className="text-[13px] vscode-text-secondary leading-relaxed"
        />
      </div>

      {/* Privacy */}
      <div>
        <p className="mb-2 text-[11px] font-semibold uppercase tracking-[0.06em] vscode-text-tertiary">
          Privacy
        </p>
        <div className="flex flex-col gap-2">
          {(
            [
              { value: 'link', label: 'Anyone with link can search' },
              { value: 'members', label: 'Only members' },
            ] as const
          ).map(({ value, label }) => (
            <label
              key={value}
              className="flex cursor-pointer items-center gap-2 text-[13px] vscode-text-secondary"
            >
              <input
                type="radio"
                name={`privacy-${network.id}`}
                value={value}
                checked={network.privacy === value}
                onChange={() => onUpdate({ privacy: value })}
                className="accent-[#10B981]"
              />
              {label}
            </label>
          ))}
        </div>
      </div>

      {/* Direct search link */}
      <div>
        <p className="mb-1.5 text-[11px] font-semibold uppercase tracking-[0.06em] vscode-text-tertiary">
          Direct search link
        </p>
        <div className="flex items-center gap-1 rounded-[8px] border px-2 py-1.5 vscode-border vscode-bg-secondary">
          <span
            className="min-w-0 flex-1 truncate font-mono text-[11px] vscode-text-secondary"
            title={network.directSearchUrl}
          >
            {network.directSearchUrl}
          </span>
          <CopyButton text={network.directSearchUrl} />
          <a
            href={network.directSearchUrl}
            target="_blank"
            rel="noreferrer"
            className="flex-shrink-0 rounded p-1 vscode-text-tertiary transition-colors hover:vscode-text-primary"
            aria-label="Open link in new tab"
            title="Open in new tab"
          >
            <ExternalLink className="h-3.5 w-3.5" aria-hidden />
          </a>
        </div>
      </div>

      {/* Destructive action — §16: text link, NEVER filled red button */}
      <div className="mt-auto border-t pt-4 vscode-border">
        <button
          type="button"
          className="text-[12px] text-red-600 underline underline-offset-2 transition-opacity hover:opacity-80 dark:text-red-400"
          onClick={() => {
            /* TODO(§8-v2): wire "manage group members" permission change */
          }}
        >
          Anyone can manage the group members
        </button>
        <p className="mt-1 text-[11px] vscode-text-tertiary">
          Enabling this allows all members to add and remove others.
        </p>
      </div>
    </aside>
  );
}
